import { assetUrl, http } from "./http";

export type ReminderChannel = "WHATSAPP" | "PAPIER";

export const REMINDER_CHANNEL_LABEL: Record<ReminderChannel, string> = {
  WHATSAPP: "WhatsApp",
  PAPIER: "Avis imprimé",
};

/** Un frais restant à payer (en retard si son échéance est passée). */
export interface UnpaidLine {
  label: string;
  /** Échéance telle que saisie dans la fiche (« Du 05/12/2026 au 05/01/2027 »). */
  dueDate: string | null;
  /** Date limite lue dans l'échéance (AAAA-MM-JJ). */
  deadline: string | null;
  remaining: number;
  overdue: boolean;
}

/** Un élève avec des frais impayés (une inscription). */
export interface UnpaidRow {
  enrollmentId: string;
  studentId: string;
  matricule: string;
  firstName: string;
  lastName: string;
  photo: string;
  classId: string;
  className: string;
  cycleId: string;
  schoolYear: string;
  /** Date de départ si l'élève a quitté l'école (sa dette reste à recouvrer). */
  leftAt: string | null;
  guardian: { name: string; phone: string; relation: string } | null;
  lines: UnpaidLine[];
  /** Somme des frais échus non payés. */
  overdueAmount: number;
  /** Reste à payer sur toute l'année. */
  totalRemaining: number;
  reminderCount: number;
  lastReminder: { channel: ReminderChannel; createdAt: string; createdByName: string } | null;
}

export interface Reminder {
  id: string;
  channel: ReminderChannel;
  amount: number;
  deadline: string | null;
  createdByName: string;
  createdAt: string;
}

interface ApiUnpaidRow extends Omit<UnpaidRow, "enrollmentId" | "studentId" | "classId" | "cycleId" | "photo"> {
  enrollmentId: number;
  studentId: number;
  classId: number;
  cycleId: number;
  photoUrl: string | null;
}

export interface UnpaidFilter {
  schoolYear?: string;
  classId?: string;
  cycleId?: string;
  /** `overdue` : échéance passée ; `unpaid` : tout reste à payer. */
  mode: "overdue" | "unpaid";
}

export async function getUnpaid(filter: UnpaidFilter): Promise<UnpaidRow[]> {
  const rows = await http.get<ApiUnpaidRow[]>("/reminders/unpaid", {
    schoolYear: filter.schoolYear,
    classId: filter.classId,
    cycleId: filter.cycleId,
    mode: filter.mode,
  });
  return rows.map(({ photoUrl, ...r }) => ({
    ...r,
    enrollmentId: String(r.enrollmentId),
    studentId: String(r.studentId),
    classId: String(r.classId),
    cycleId: String(r.cycleId),
    photo: assetUrl(photoUrl),
  }));
}

/** Enregistre une relance pour chaque inscription (avis imprimés pour une classe, ou un message WhatsApp). */
export async function recordReminders(
  enrollmentIds: string[],
  channel: ReminderChannel,
  deadline?: string,
): Promise<number> {
  return (
    await http.post<{ count: number }>("/reminders", {
      enrollmentIds: enrollmentIds.map(Number),
      channel,
      deadline: deadline || undefined,
    })
  ).count;
}

export async function getReminderHistory(studentId: string): Promise<Reminder[]> {
  return (await http.get<Array<Reminder & { id: number }>>("/reminders", { studentId })).map((r) => ({
    ...r,
    id: String(r.id),
  }));
}

/**
 * Numéro WhatsApp international (chiffres seuls) : « 620 11 22 33 » → « 224620112233 ».
 * Sans indicatif, celui de la Guinée (224) est ajouté.
 */
export function whatsappNumber(phone: string): string | null {
  let digits = phone.replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.length === 9) digits = `224${digits}`;
  return digits.length >= 11 ? digits : null;
}
